import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import {
  districts,
  states,
  stars,
  raasis,
  maritalStatusOptions,
  idProofTypes,
  panCardOptions,
} from "../utils/formDataUtils";
import { useMutation } from "@apollo/client";
import { EDIT_MEMBER_MUTATION } from "../graphql/mutations";
import { Snackbar, Alert } from "@mui/material";

const EditUserModal = ({ isOpen, onClose, user }) => {
  const [formData, setFormData] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [editMember] = useMutation(EDIT_MEMBER_MUTATION);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [submittedFields, setSubmittedFields] = useState({});

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  useEffect(() => {
    if (isOpen && user) {
      setFormData(user);
      setFormErrors({});
      
      // Track which fields have been previously submitted
      const fieldsSubmitted = {};
      
      // Check which credential fields already have values from previous submissions
      if (user.ID_Proof_Type && user.ID_Proof_Number) {
        fieldsSubmitted.ID_Proof_Type = true;
        fieldsSubmitted.ID_Proof_Number = true;
      }
      
      if (user.Pan_Card && user.Pan_Number) {
        fieldsSubmitted.Pan_Card = true;
        fieldsSubmitted.Pan_Number = true;
      }
      
      setSubmittedFields(fieldsSubmitted);
    }
  }, [isOpen, user?.id]);

const handleOnChange = (e) => {
  const { name, value } = e.target;

  let updatedValue = value;

  if (name === "ID_Proof_Number") {
    const idType = (formData.ID_Proof_Type || "").trim().toLowerCase();
    if (idType === "aadhaar") {
      updatedValue = value.replace(/\D/g, ""); // Remove non-digits
    }
  }

  // Force uppercase for PAN and ID numbers
  if (name === "Pan_Number" || name === "ID_Proof_Number") {
    updatedValue = value.toUpperCase();
  }

  if (name === "Pan_Card" && value === "No") {
    setFormData(prev => ({
      ...prev,
      [name]: updatedValue,
      Pan_Number: "" // Clear the Pan_Number when Pan_Card is "No"
    }));
  } else {
    // Original code for other fields
    setFormData((prev) => ({
      ...prev,
      [name]: updatedValue,
    }));
  }

  setFormErrors((prev) => ({
    ...prev,
    [name]: "",
  }));
};

const validateForm = () => {
  const errors = {};
  
  // Define non-required fields - add Pan_Number to this list
  const nonRequiredFields = ["Star", "Raasi", "Kootram", "Kuladeivam", "Pan_Number", "Email"];
  
  // Check all fields except non-required ones
  fields.forEach(field => {
    if (!nonRequiredFields.includes(field) && field !== "id" && !formData[field]) {
      errors[field] = `${field.replaceAll("_", " ")} is required`;
    }
  });

  if (formData.Pan_Card !== "Yes" && formData.Pan_Card !== "No") {
    errors.Pan_Card = "PAN Card is required";
  }

  // Specific validation for Pan_Number only when Pan_Card is "Yes"
  if (formData.Pan_Card === "Yes" && !formData.Pan_Number) {
    errors.Pan_Number = "PAN Number is required";
  }

    if (formData.ID_Proof_Type && !formData.ID_Proof_Number) {
      errors.ID_Proof_Number = "ID Proof Number is required";
    }

    // Add additional validation for ID Proof Number format
    if (formData.ID_Proof_Type && formData.ID_Proof_Number) {
      const idProofType = formData.ID_Proof_Type.trim().toLowerCase();
      const idProofNumber = formData.ID_Proof_Number.trim();

      if (idProofType === "aadhaar") {
        if (!/^\d{12}$/.test(idProofNumber)) {
          errors.ID_Proof_Number = "Aadhaar number must be exactly 12 digits";
        }
      } else if (idProofType === "license") {
        if (idProofNumber.length !== 15) {
          errors.ID_Proof_Number = "License number must be exactly 15 characters";
        }
      }
    }


    // Add additional validation for PAN Number format
    if (formData.Pan_Card === "Yes" && formData.Pan_Number) {
      // Simple validation for PAN (10 alphanumeric characters)
      if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.Pan_Number)) {
        errors.Pan_Number = "Invalid PAN format (e.g., ABCDE1234F)";
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    window.scrollTo(0, 0);
    if (!validateForm()) return;
  
    try {
      const formattedDob = new Date(formData.DOB).toLocaleDateString("en-CA");
      
      // Check conditions for special handling
      
      
      // Create mutation variables object
      const mutationVariables = {
        id: formData.id,
        First_Name: formData.First_Name,
        Last_Name: formData.Last_Name,
        Mobile_Number: formData.Mobile_Number,
        DOB: formattedDob,
        City: formData.City,
        District: formData.District,
        State: formData.State,
        Pincode: formData.Pincode,
        Email: formData.Email,
        Occupation: formData.Occupation,
        Address: formData.Address,
        Marital_Status: formData.Marital_Status,
        ID_Proof_Type: formData.ID_Proof_Type,
        ID_Proof_Number: formData.ID_Proof_Number,
        Pan_Card: formData.Pan_Card,
        Family_Details: formData.Family_Details,
      };
  
      // Handle PAN Number
      if (formData.Pan_Card === "Yes") {
        mutationVariables.Pan_Number = formData.Pan_Number || "";
      } else {
        mutationVariables.Pan_Number = "";
      }
  
      // Handle optional fields (these can always be sent as they are)
      mutationVariables.Star = formData.Star || "";
      mutationVariables.Raasi = formData.Raasi || "";
      mutationVariables.Kootram = formData.Kootram || "";
      mutationVariables.Kuladeivam = formData.Kuladeivam || "";
  
  
      const { data: mutationData } = await editMember({
        variables: mutationVariables,
      });
  
      if (mutationData?.editMember) {
        // After successful submission, mark ID fields as submitted
        const newSubmittedFields = { ...submittedFields };
        
        if (formData.ID_Proof_Type && formData.ID_Proof_Number) {
          newSubmittedFields.ID_Proof_Type = true;
          newSubmittedFields.ID_Proof_Number = true;
        }
        
        // Only mark PAN fields as submitted if Pan_Card is "Yes" and there's a Pan_Number
        if (formData.Pan_Card === "Yes" && formData.Pan_Number) {
          newSubmittedFields.Pan_Card = true;
          newSubmittedFields.Pan_Number = true;
        }
        
        setSubmittedFields(newSubmittedFields);
        
        setSnackbarMessage("Member updated successfully!");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        onClose();
      }
    } catch (error) {
      console.error("Error updating member:", error);
      const apiMessage =
        error?.graphQLErrors?.[0]?.message || "Failed to update member.";
      setSnackbarMessage(apiMessage);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

   const getFieldInput = (field) => {

          let maxLength;

      if (field === "ID_Proof_Number") {
        const idType = (formData.ID_Proof_Type || "").trim().toLowerCase();
        if (idType === "aadhaar") maxLength = 12;
        if (idType === "license") maxLength = 15;
      }

      if (field === "Pan_Number") {
        maxLength = 10;
      }

    const dropdownMap = {
      District: districts,
      State: states,
      Star: stars,
      Raasi: raasis,
      Marital_Status: maritalStatusOptions,
      ID_Proof_Type: idProofTypes,
      Pan_Card: panCardOptions,
    };

    const label =
      field === "id"
        ? "ID"
        : field === "Kootram"
          ? "Gothram"
          : field.replaceAll("_", " ");

    const isDropdown = Object.keys(dropdownMap).includes(field);
    
    // Define read-only fields
    const alwaysReadOnly = [
      "First_Name", 
      "Last_Name", 
      "Mobile_Number", 
      "DOB", 
      "City", 
      "Pincode"
    ];
    
    const isReadOnly = alwaysReadOnly.includes(field);
    
    // Disable ID field and specific fields that have been previously submitted
    const isDisabled = field === "id" || 
      field === "State" ||
      field === "District" ||
      (field === "ID_Proof_Type" && submittedFields.ID_Proof_Type) ||
      (field === "ID_Proof_Number" && submittedFields.ID_Proof_Number) ||
      (field === "Pan_Card" && submittedFields.Pan_Card) ||
      (field === "Pan_Number" && submittedFields.Pan_Number);
      
    // Check if field is required (all except Star, Raasi, Kootram, Kuladeivam)
    const nonRequiredFields = ["Star", "Raasi", "Kootram", "Kuladeivam", "Email"];
    const isRequired = !nonRequiredFields.includes(field) && field !== "id";

    return (
      <div key={field} className="flex flex-col space-y-1">
        <label
          htmlFor={field}
          className="text-sm font-medium text-gray-700 text-left"
        >
          {label} {isRequired && <span className="text-red-500">*</span>}
        </label>

        {isDropdown ? (
          <select
            name={field}
            id={field}
            value={formData[field] || ""}
            onChange={handleOnChange}
            disabled={isDisabled}
            required={isRequired}
            className={`mt-1 px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 ${isDisabled ? "bg-gray-100 cursor-not-allowed text-gray-500" : ""}`}
          >
            <option value="">Select {label}</option>
            {dropdownMap[field].map((option) => {
              const value = typeof option === "string" ? option : option.value;
              const labelText = typeof option === "string" ? option : option.label;
              return (
                <option key={value} value={value}>
                  {labelText}
                </option>
              );
            })}
          </select>
        ) : (


          <input
            type="text"
            name={field}
            id={field}
            value={formData[field] || ""}
            onChange={handleOnChange}
            readOnly={isReadOnly}
            disabled={isDisabled}
            maxLength={maxLength}
            required={isRequired}
           
            className={`mt-1 px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 ${isReadOnly || isDisabled
              ? "bg-gray-100 cursor-not-allowed text-gray-500"
              : ""
              }`}
          />
        )}

        {formErrors[field] && (
          <p className="text-sm text-red-500 text-left pl-1">{formErrors[field]}</p>
        )}
      </div>
    );
  };

  const fields = [
    "id",
    "First_Name",
    "Last_Name",
    "Mobile_Number",
    "Email",
    "DOB",
    "Occupation",
    "Address",
    "City",
    "District",
    "State",
    "Pincode",
    "Marital_Status",
    "Family_Details",
    "ID_Proof_Type",
    "ID_Proof_Number",
    "Pan_Card",
    "Pan_Number",
    "Star",
    "Raasi",
    "Kootram",
    "Kuladeivam",
  ];

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.3 }}
            className="fixed top-0 right-0 w-full max-w-xl pt-24 h-full bg-[#f8f9e9] shadow-xl z-50 overflow-y-auto"
          >

            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-xl font-semibold">Edit Member Profile</h2>
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-800"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-5">
             {fields.map((field) => {
                const idProofType = (formData.ID_Proof_Type || "").trim().toLowerCase();
                const panCard = (formData.Pan_Card || "").trim().toLowerCase();

                if (field === "ID_Proof_Number") {
                  if (idProofType !== "aadhaar" && idProofType !== "license") {
                    return null;
                  }
                }

                if (field === "Pan_Number") {
                  if (panCard !== "yes") {
                    return null;
                  }
                }

  return getFieldInput(field);
})}



            </div>

            <div className="p-4 border-t flex justify-end gap-4">
              <button
                onClick={onClose}
                style={{ backgroundColor: '#E52029', borderRadius: '999px' }}
                className="px-4 py-2 text-white hover:opacity-90"
              >
                Cancel
              </button>

              <button
                onClick={handleSubmit}
                style={{ backgroundColor: "green", borderRadius: '999px' }}
                className="px-4 py-2 text-white hover:opacity-90"
              >
                Save Changes
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default EditUserModal;